import { type CSSProperties, type MouseEvent as ReactMouseEvent, useState, useRef, memo } from "react";
import {
	moveClipAction, updateClipStartAction, updateClipEndAction, setClipDraggingAction,
	selectScrollTimelineStart, selectScrollTimelineEnd, selectZoomLevel
} from "Data/Redux/Slices/Playback";
import { selectClipDragging, selectClipEnd, selectClipStart } from "Data/Redux/Slices/Playback";
import "Components/PlaybackLayout/ClipArea/ClipArea.less";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { Icon, IconSize } from "@clintonelec/react-storybook";
import { type DateString, DateUnits } from "@clintonelec/typescriptutils";

enum DragTarget {
	LEFT_HANDLE = "LEFT_HANDLE",
	RIGHT_HANDLE = "RIGHT_HANDLE",
	CENTER = "CENTER"
}

const ClipArea = () => {
	const timelineStart = new Date(useAppSelector(selectScrollTimelineStart));
	const timelineEnd = new Date(useAppSelector(selectScrollTimelineEnd));
	const [ dragTarget, setDragTarget ] = useState<DragTarget | null>();
	const [ msPerPixel, setMsPerPixel ] = useState(0);
	const clipContainerRef = useRef<HTMLDivElement>(null);
	const clipStart = new Date(useAppSelector(selectClipStart));
	const clipEnd = new Date(useAppSelector(selectClipEnd));
	const clipDragging = useAppSelector(selectClipDragging);
	const zoomLevel = useAppSelector(selectZoomLevel);
	const radius = 12;
	const dispatch = useAppDispatch();
	const moveClip = (date: DateString) => dispatch(moveClipAction(date));
	const setClipDragging = (dragging: boolean) => dispatch(setClipDraggingAction(dragging));
	const updateClipStart = (date: DateString) => dispatch(updateClipStartAction(date));
	const updateClipEnd = (date: DateString) => dispatch(updateClipEndAction(date));
	const leftPercent =
		(clipStart.getTime() - timelineStart.getTime()) /
		(timelineEnd.getTime() - timelineStart.getTime());

	const widthPercent =
		(clipEnd.getTime() - clipStart.getTime()) /
		(timelineEnd.getTime() - timelineStart.getTime());

	const customStyles: CSSProperties = {
		left: `${ (leftPercent * 100) }%`,
		width: `${ widthPercent * 100 }%`
	};

	const handleMouseDown = (activeDragTarget: DragTarget) => (event: ReactMouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (!(event.buttons & 1)) {
			return;
		}

		setClipDragging(true);
		setDragTarget(activeDragTarget);

		if (clipContainerRef.current) {
			setMsPerPixel(
				(zoomLevel * DateUnits.MINUTES * radius * 2) /
				clipContainerRef.current.clientWidth
			);
		}

		document.onmouseup = handleMouseUp;
	};

	const handleMouseMove = (event: ReactMouseEvent) => {
		event.preventDefault();
		event.stopPropagation();

		if (!clipDragging || !clipContainerRef.current) {
			return;
		}

		persistDragState(event.clientX - clipContainerRef.current.getBoundingClientRect().left);
	};

	const handleMouseUp = (event: MouseEvent) => {
		if (event.buttons & 1) {
			return;
		}

		setClipDragging(false);
		setDragTarget(null);

		document.onmouseup = null;
	};

	const persistDragState = (offsetX: number) => {
		const offset = offsetX * msPerPixel;

		switch (dragTarget) {
			case DragTarget.CENTER:
				moveClip(new Date(timelineStart.getTime() + offset).toISOString());

				break;
			case DragTarget.LEFT_HANDLE:
				updateClipStart(new Date(timelineStart.getTime() + offset).toISOString());

				break;
			case DragTarget.RIGHT_HANDLE:
				updateClipEnd(new Date(timelineStart.getTime() + offset).toISOString());

				break;
		}
	};

	return (
		<div
			className="clip-overlay"
			ref={ clipContainerRef }
			onMouseMove={ handleMouseMove }
		>
			<div
				className="clip-area-container"
				style={ customStyles }
			>
				<div
					className="clip-area"
					onMouseDown={ handleMouseDown(DragTarget.CENTER) }
				/>
				<div
					className="clip-handle-left"
					onMouseDown={ handleMouseDown(DragTarget.LEFT_HANDLE) }
				>
					<Icon name="ellipsis-v" size={ IconSize.SMALLER } className="handle-icon" />
				</div>
				<div
					className="clip-handle-right"
					onMouseDown={ handleMouseDown(DragTarget.RIGHT_HANDLE) }
				>
					<Icon name="ellipsis-v" size={ IconSize.SMALLER } className="handle-icon" />
				</div>
			</div>
		</div>
	);
};

export default memo(ClipArea);
